import React, { useState } from "react";
import {
  Stack,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
} from "@mui/material";
import { Fade } from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { PlanOption } from "@volatile/pages/OrderTracker/pages/PlanReviewPage/PlanReviewPage";
import type { OrderOut } from "@providers/hop-ord-server/api";
import {
  DVHCard,
  PlanSelector,
  Scorecard,
  getSinglePlanScorecardData,
  VideoWalkthrough,
} from "@volatile/components";
import { toLocaleDateTimeString } from "@utils";
import { getCompletedPlanTimestamps } from "@volatile/pages/OrderTracker/components";

interface PlanSelectionHook {
  selectedPlanOption: string;
  setSelectedPlanOption: (value: string) => void;
  selectedPlan: string;
  setSelectedPlan: (value: string) => void;
  selectedPlanAOption: string;
  setSelectedPlanAOption: (value: string) => void;
  selectedPlanBOption: string;
  setSelectedPlanBOption: (value: string) => void;
  planOptions: PlanOption[];
  getPlanDisplayName: (plan: string) => string;
  getAvailablePlanAOptions: () => PlanOption[];
  getAvailablePlanBOptions: () => PlanOption[];
}

interface SinglePlanViewProps {
  orderId: number;
  orderData: OrderOut | undefined;
  scorecardAttachmentsLoading: boolean;
  dateLabel?: string;
  planSelection: PlanSelectionHook;
  planGenerationData?: unknown[];
}

const SinglePlanView: React.FC<SinglePlanViewProps> = ({
  orderId,
  orderData,
  scorecardAttachmentsLoading,
  dateLabel,
  planSelection,
  planGenerationData: _planGenerationData,
}) => {
  // Plan Summary state and handlers
  const [isEditing, setIsEditing] = useState(false);

  // Function to get the timestamp for the selected plan
  const getPlanTimestamp = (planKey: string) => {
    // Always use localStorage timestamps to match CompletedPlans table (ignore planGenerationData)
    const timestamps = getCompletedPlanTimestamps();

    // Explicit mapping to ensure consistency with CompletedPlans table
    // planA → "Prostate Nodes R1" → plan1FinishedAt
    // planB → "Prostate Nodes R2" → plan2FinishedAt
    if (planKey === "planA") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planKey === "planB") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    // Fallback by plan display name
    const planDisplayName = planSelection.getPlanDisplayName(planKey);
    if (planDisplayName === "Prostate Nodes R1") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planDisplayName === "Prostate Nodes R2") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    return "Mar-17-2024, 3:44 PM"; // Final fallback
  };

  // Dynamic summary based on selected plan
  const getPlanSummary = (planOption: string) => {
    switch (planOption) {
      case "planA":
        return "This plan achieves the required target coverage across CTV’s and PTV’s while achieving all Rectum and Bowel constraints. There are minor deviations on the Bladder which is expected given the overlap with the Targets. All other organs at risk are within the specified tolerance";
      case "planB":
        return "This plan achieves the required target coverage across CTV’s and PTV’s, however the rectum dose is higher than Plan A. The volume of bowel receiving 45Gy is higher and Bladder doses are in the minor deviation region. All other organs at risk are within the specified tolerance.";
    }
  };

  const [summaryText, setSummaryText] = useState(
    getPlanSummary(planSelection.selectedPlanOption),
  );
  const [editText, setEditText] = useState(summaryText);

  // Update summary when plan selection changes
  React.useEffect(() => {
    if (!isEditing) {
      const newSummary = getPlanSummary(planSelection.selectedPlanOption);
      setSummaryText(newSummary);
      setEditText(newSummary);
    }
  }, [planSelection.selectedPlanOption, isEditing]);

  const handleEdit = () => {
    setEditText(summaryText);
    setIsEditing(true);
  };

  const handleSave = () => {
    setSummaryText(editText);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditText(summaryText);
    setIsEditing(false);
  };

  return (
    <Fade in timeout={500}>
      <Stack>
        {/* Plan Selector */}
        <PlanSelector
          mode="single"
          selectedPlanOption={planSelection.selectedPlanOption}
          onPlanOptionChange={planSelection.setSelectedPlanOption}
          planOptions={planSelection.planOptions}
          dateLabel={dateLabel}
          singlePlanTimestamp={getPlanTimestamp(
            planSelection.selectedPlanOption,
          )}
        />

        {/* Plan Summary */}
        <Stack
          sx={{
            marginTop: 2,
            marginBottom: 4,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <div style={{ width: "100%" }}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                marginBottom={2}
              >
                <Typography variant="h6">Summary</Typography>
                <Chip
                  label="Generated by AI"
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    borderColor: "#374151 !important",
                    color: "#374151 !important",
                    "& .MuiChip-label": {
                      color: "#374151 !important",
                    },
                    "&.MuiChip-outlined": {
                      borderColor: "#374151 !important",
                    },
                  }}
                />
                {!isEditing ? (
                  <IconButton size="small" onClick={handleEdit}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Stack direction="row" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={handleSave}
                      color="primary"
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancel}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
              <div style={{ width: "100%", position: "relative" }}>
                <Typography
                  variant="body2"
                  style={{
                    width: "100%",
                    visibility: isEditing ? "hidden" : "visible",
                    minHeight: isEditing ? "120px" : "auto",
                  }}
                >
                  {summaryText}
                </Typography>
                {isEditing && (
                  <TextField
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    multiline
                    rows={4}
                    variant="outlined"
                    size="small"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      width: "100%",
                    }}
                  />
                )}
              </div>
            </div>
          </Box>
        </Stack>

        {/* Plan Review Summary */}
        {orderData?.planReviewSummary && (
          <Stack marginBottom={2}>
            <Typography fontWeight={500} display="inline">
              Summary:{" "}
            </Typography>
            <Typography variant="body1" display="inline">
              {orderData.planReviewSummary}
            </Typography>
          </Stack>
        )}

        {/* Scorecard */}
        <Stack sx={{ marginBottom: 3 }}>
          <Scorecard
            mode="single"
            data={getSinglePlanScorecardData(planSelection.selectedPlanOption)}
            isLoading={scorecardAttachmentsLoading}
          />
        </Stack>

        {/* DVH Card */}
        <Stack sx={{ marginBottom: 3 }}>
          <DVHCard isComparisonMode={false} />
        </Stack>

        {/* Plan Walkthrough */}
        <Stack sx={{ marginBottom: 4 }}>
          <VideoWalkthrough orderId={orderId} isComparisonMode={false} />
        </Stack>
      </Stack>
    </Fade>
  );
};

export default SinglePlanView;
